
import React, { useState } from 'react';
import { User, UserRole, ArenaConfig } from '../types';
import { X, LogIn, UserPlus, Zap, Shield, Mail, Loader2, AlertCircle } from 'lucide-react';
import { handleGoogleLogin, handleFacebookLogin, supabase } from '../services/supabase';

interface AuthModalProps {
  onLogin: (user: User) => void;
  onClose: () => void;
  config: ArenaConfig;
}

export const AuthModal: React.FC<AuthModalProps> = ({ onLogin, onClose, config }) => {
  const [isRegister, setIsRegister] = useState(false);
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setErrorMsg('');

    try {
        if (isRegister) {
            // REGISTRIERUNG
            // Wir versuchen, den User neu anzulegen und den Tenant direkt in die Metadaten zu schreiben.
            const { data, error } = await supabase.auth.signUp({
                email,
                password,
                options: {
                    data: {
                        full_name: name
                    }
                }
            });

            if (error) {
                if (error.message.includes('already registered')) {
                    // User existiert bereits global (andere Arena) - Login + Tenant-Verknüpfung
                    const { data: loginData, error: loginError } = await supabase.auth.signInWithPassword({
                        email,
                        password
                    });

                    if (loginError) {
                        throw new Error("Dieser Benutzer existiert bereits, aber das Passwort ist falsch.");
                    }

                    if (loginData.user) {
                        // Tenant-Zuordnung in sichere DB-Tabelle schreiben (RLS-geschützt)
                        const { error: tenantError } = await supabase
                            .from('user_tenants')
                            .upsert({ user_id: loginData.user.id, tenant_id: config.tenantId }, 
                                     { onConflict: 'user_id,tenant_id' });
                        if (tenantError) console.error("Tenant assignment error:", tenantError);
                    }
                } else {
                    throw error;
                }
            } else if (data?.user) {
                // Neuer User erfolgreich erstellt - Tenant-Zuordnung speichern
                const { error: tenantError } = await supabase
                    .from('user_tenants')
                    .insert({ user_id: data.user.id, tenant_id: config.tenantId });
                if (tenantError) console.error("Tenant assignment error:", tenantError);
            }
        } else {
            // LOGIN
            const { data, error } = await supabase.auth.signInWithPassword({
                email,
                password
            });

            if (error) throw error;

            // Check Tenant Access via secure DB table (not user_metadata)
            if (data.user) {
                const isAdmin = data.user.app_metadata?.roles?.[config.tenantId] === 'ADMIN' || 
                                data.user.app_metadata?.role === 'SUPER_ADMIN';
                
                if (!isAdmin) {
                    const { data: tenantAccess, error: tenantCheckError } = await supabase
                        .from('user_tenants')
                        .select('tenant_id')
                        .eq('user_id', data.user.id)
                        .eq('tenant_id', config.tenantId)
                        .maybeSingle();
                    
                    if (tenantCheckError || !tenantAccess) {
                        await supabase.auth.signOut();
                        throw new Error("Sie sind für diese Arena noch nicht registriert. Bitte wechseln Sie auf 'Registrieren'.");
                    }
                }
            }
        }
    } catch (err: any) {
        console.error("Auth Error:", err);
        setErrorMsg(err.message || "Ein Fehler ist aufgetreten.");
        setIsLoading(false);
    }
  };

  const loginWithDemoEmail = (emailVal: string, nameVal: string, role: UserRole) => {
    // Demo-Buttons nur für lokale Simulation nutzen, wenn keine echte DB verbunden ist
    // oder um das UI zu testen. In Produktion werden diese Buttons idealerweise ausgeblendet
    // oder loggen echte Demo-User ein.
    const guestUser: User = {
        id: emailVal,
        name: nameVal || emailVal.split('@')[0],
        email: emailVal,
        role: role,
        tenantId: config.tenantId
    };
    onLogin(guestUser);
  };

  const SocialButton = ({ provider, icon, onClick }: { provider: string, icon: React.ReactNode, onClick?: () => void }) => (
    <button onClick={onClick} className="flex-1 py-2.5 border border-slate-200 rounded-lg hover:bg-slate-50 transition font-medium text-sm text-slate-600 flex items-center justify-center gap-2 bg-white shadow-sm">
      {icon}
      {provider}
    </button>
  );

  const GoogleLogo = () => (
    <svg className="w-5 h-5" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
      <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" />
      <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
      <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
      <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
    </svg>
  );

  const FacebookLogo = () => (
    <svg className="w-5 h-5" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
      <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" fill="#1877F2" />
    </svg>
  );

  return (
    <div className="fixed inset-0 z-[160] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden animate-fade-in flex flex-col max-h-[90vh]">
        
        <div className="flex items-center justify-between p-6 border-b border-slate-100 bg-slate-50">
          <h2 className="text-xl font-bold text-slate-800 flex items-center gap-2">
            {isRegister ? <UserPlus className="text-emerald-600" /> : <LogIn className="text-emerald-600" />}
            {isRegister ? 'Registrieren' : 'Anmelden'}
          </h2>
          <button onClick={onClose} className="p-2 hover:bg-slate-200 rounded-full transition-colors text-slate-500">
            <X size={20} />
          </button>
        </div>

        <div className="p-6 overflow-y-auto">
          
          {/* Demo Section - Only visible if environment variable allows or dev mode */}
          {(!process.env.NODE_ENV || process.env.NODE_ENV === 'development') && (
              <div className="bg-amber-50 p-4 rounded-xl border border-amber-100 mb-6">
                 <p className="text-[10px] font-bold text-amber-700 uppercase mb-2 tracking-widest flex items-center gap-2"><AlertCircle size={12}/> Demo-Modus (Lokal)</p>
                 <div className="flex gap-2">
                    <button onClick={() => loginWithDemoEmail('max@gast.de', 'Max Mustermann', UserRole.GUEST)} className="flex-1 py-2 bg-white border border-amber-200 rounded text-xs font-bold text-amber-800 hover:bg-amber-100">Gast</button>
                    <button onClick={() => loginWithDemoEmail('admin@sportarena.de', 'Chef', UserRole.ADMIN)} className="flex-1 py-2 bg-white border border-amber-200 rounded text-xs font-bold text-amber-800 hover:bg-amber-100">Admin</button>
                 </div>
              </div>
          )}

          {errorMsg && (
            <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-xl text-xs font-bold mb-4 flex items-start gap-2">
                <AlertCircle size={16} className="shrink-0 mt-0.5" />
                <span>{errorMsg}</span>
            </div>
          )}

          <div className="space-y-3 mb-6">
            <p className="text-xs font-bold text-slate-400 uppercase text-center mb-2">Social Login</p>
            <div className="flex gap-3">
              {(config.socialProviders || []).includes('google') && <SocialButton provider="Google" icon={<GoogleLogo />} onClick={handleGoogleLogin} />}
              {(config.socialProviders || []).includes('facebook') && <SocialButton provider="Facebook" icon={<FacebookLogo />} onClick={handleFacebookLogin} />}
            </div>
          </div>

          <div className="relative flex py-4 items-center">
            <div className="flex-grow border-t border-slate-100"></div>
            <span className="flex-shrink-0 mx-4 text-slate-400 text-[10px] font-bold uppercase tracking-widest">Email Login</span>
            <div className="flex-grow border-t border-slate-100"></div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {isRegister && (
              <div className="animate-fade-in">
                <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Name</label>
                <input 
                  type="text" 
                  required 
                  className="w-full px-3 py-2.5 border border-slate-200 rounded-xl focus:ring-2 focus:ring-emerald-500 outline-none transition bg-white font-medium"
                  placeholder="Ihr Name"
                  value={name}
                  onChange={e => setName(e.target.value)}
                />
              </div>
            )}
            
            <div>
              <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Email Adresse</label>
              <div className="relative">
                <Mail className="absolute left-3 top-3 text-slate-400" size={16} />
                <input 
                  type="email" 
                  required 
                  className="w-full pl-10 pr-3 py-2.5 border border-slate-200 rounded-xl focus:ring-2 focus:ring-emerald-500 outline-none transition bg-white font-medium"
                  placeholder="name@sport.de"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Passwort</label>
              <input 
                type="password" 
                required 
                className="w-full px-3 py-2.5 border border-slate-200 rounded-xl focus:ring-2 focus:ring-emerald-500 outline-none transition bg-white font-medium"
                placeholder="••••••••"
                value={password}
                onChange={e => setPassword(e.target.value)}
              />
            </div>

            <button 
              type="submit"
              disabled={isLoading}
              className="w-full theme-bg-secondary text-white py-3.5 rounded-xl font-bold hover:opacity-90 transition shadow-lg mt-2 flex items-center justify-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed"
            >
              {isLoading ? <Loader2 className="animate-spin" size={18} /> : (isRegister ? 'Registrieren & Beitreten' : 'Einloggen')}
            </button>

            <div className="text-center pt-2">
              <button 
                type="button"
                onClick={() => { setIsRegister(!isRegister); setErrorMsg(''); }}
                className="text-xs text-slate-500 hover:text-emerald-600 font-bold underline decoration-slate-300 underline-offset-4"
              >
                {isRegister ? 'Bereits Member? Hier anmelden' : 'Noch kein Account? Jetzt registrieren'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};
